import { Injectable } from '@angular/core';
import {
  PulseConfigurationKeys,
  PulseConfigurations,
  PULSE_FEATURE_NAME,
} from '@epicuro-next/constants/rule-engine/pulse-rules';
import { IUser } from '@epicuro-next/data/users';
import { ConfigurationsService } from '@epicuro-next/platform/configuration';
import { IGtmProperties } from '@epicuro-next/platform/tracking';
import {
  Account,
  Basket,
  Product,
  ProductPriceRanges,
} from 'graphql-api/gql-api-interfaces';
import { EMPTY, iif, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import { IEcommerceBasketItem } from '../interfaces/ecommerce-basket-item';
import { multipleItemsMapper } from '../mappers/multiple-items.mapper';
import { singleItemMapper } from '../mappers/single-item.mapper';

const NOOP_DEFAULT_MAPPER_FUNCTION = `function mapFunction() { return {} }`;

@Injectable()
export class EcommerceTrackingMapperService {
  constructor(private configService: ConfigurationsService) {}

  private basketMapperFunction$ = this.configService.getConfigValue<
    PulseConfigurations,
    PulseConfigurationKeys.GTMEcommerceBasketProductItemMapper
  >({
    featureName: PulseConfigurationKeys.GTMEcommerceBasketProductItemMapper,
    namespace: PULSE_FEATURE_NAME,
    defaultValue: NOOP_DEFAULT_MAPPER_FUNCTION,
  });

  private regularMapperFunction$ = this.configService.getConfigValue<
    PulseConfigurations,
    PulseConfigurationKeys.GTMEcommerceRegularProductItemMapper
  >({
    featureName: PulseConfigurationKeys.GTMEcommerceRegularProductItemMapper,
    namespace: PULSE_FEATURE_NAME,
    defaultValue: NOOP_DEFAULT_MAPPER_FUNCTION,
  });

  private purchaseExtrasMapperMapperFunction$ =
    this.configService.getConfigValue<
      PulseConfigurations,
      PulseConfigurationKeys.GTMEcommercePurchaseExtrasMapper
    >({
      featureName: PulseConfigurationKeys.GTMEcommercePurchaseExtrasMapper,
      namespace: PULSE_FEATURE_NAME,
      defaultValue: NOOP_DEFAULT_MAPPER_FUNCTION,
    });

  private purchaseActionFieldMapperFunction$ =
    this.configService.getConfigValue<
      PulseConfigurations,
      PulseConfigurationKeys.GTMEcommercePurchaseActionFieldMapper
    >({
      featureName: PulseConfigurationKeys.GTMEcommercePurchaseActionFieldMapper,
      namespace: PULSE_FEATURE_NAME,
      defaultValue: NOOP_DEFAULT_MAPPER_FUNCTION,
    });

  private checkBasketActionFieldMapperFunction$ =
    this.configService.getConfigValue<
      PulseConfigurations,
      PulseConfigurationKeys.GTMEcommerceCheckBasketActionFieldMapper
    >({
      featureName:
        PulseConfigurationKeys.GTMEcommerceCheckBasketActionFieldMapper,
      namespace: PULSE_FEATURE_NAME,
      defaultValue: NOOP_DEFAULT_MAPPER_FUNCTION,
    });

  public mapBasketProducts(products: IEcommerceBasketItem[]) {
    return this.basketMapperFunction$.pipe(
      take(1),
      switchMap((mapper: string) => {
        return this.decideMultipleItemsMapper(products, mapper);
      }),
    );
  }

  public mapRegularProducts(
    data: { product: Product; prices: ProductPriceRanges }[],
  ) {
    return this.regularMapperFunction$.pipe(
      take(1),
      switchMap((mapper: string) => {
        return this.decideMultipleItemsMapper(data, mapper);
      }),
    );
  }

  public mapPurchaseExtras(data: {
    user: IUser | null;
    account: Account | null;
    session: {
      siteId: string | null;
      subSiteId: string | null;
    } | null;
    basket: Basket | null;
  }) {
    return this.purchaseExtrasMapperMapperFunction$.pipe(
      take(1),
      switchMap((mapper: string) => {
        return this.decideSingleItemMapper(data, mapper);
      }),
    );
  }

  public mapPurchaseActionField(data: { basket: Basket | null }) {
    return this.purchaseActionFieldMapperFunction$.pipe(
      take(1),
      switchMap((mapper: string) => {
        return this.decideSingleItemMapper(data, mapper);
      }),
    );
  }

  public mapCheckBasketActionField(data: { statusValid: boolean }) {
    return this.checkBasketActionFieldMapperFunction$.pipe(
      take(1),
      switchMap((mapper: string) => {
        return this.decideSingleItemMapper(data, mapper);
      }),
    );
  }

  public mapSendTrackEventData(
    eventName: string,
    payload: IGtmProperties,
    mapper: string,
  ) {
    return this.decideSingleItemMapper({ eventName, ...payload }, mapper);
  }

  public mapEventsFromAction(payload: unknown, mapper: string | undefined) {
    if (!mapper) {
      return EMPTY;
    }

    return this.decideSingleItemMapper(payload, mapper).pipe(
      map((x) => x as string[]),
    );
  }

  private decideMultipleItemsMapper(data: unknown[], mapper: string) {
    return iif(
      () => !!mapper && mapper !== NOOP_DEFAULT_MAPPER_FUNCTION,
      multipleItemsMapper(data, mapper),
      of([]),
    );
  }

  private decideSingleItemMapper(data: unknown, mapper: string) {
    return iif(
      () => !!mapper && mapper !== NOOP_DEFAULT_MAPPER_FUNCTION,
      singleItemMapper(data, mapper),
      of({}),
    );
  }
}
